import { useEffect, useRef } from "react";
import type { EChartsOption } from "echarts";
import * as echarts from "echarts/core";
import { BarChart, LineChart, ScatterChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import "echarts-wordcloud";

echarts.use([
  BarChart,
  LineChart,
  ScatterChart,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  CanvasRenderer,
]);

const colours = {
  ink: "#1c211f",
  muted: "#6c7470",
  grid: "#e5e5df",
  green: "#176b5b",
  greenLight: "#73a093",
  teal: "#2b8591",
  blue: "#527d9a",
  amber: "#b4782d",
  red: "#a84b3f",
  paper: "#fbfbf8",
} as const;

const sharedTextStyle = {
  fontFamily: "DM Sans, sans-serif",
  color: colours.muted,
};

function EChart({
  option,
  className,
  label,
}: {
  option: EChartsOption;
  className?: string;
  label: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const chart = echarts.init(element, undefined, { renderer: "canvas" });
    chart.setOption(option);
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(element);

    return () => {
      observer.disconnect();
      chart.dispose();
    };
  }, [option]);

  return (
    <div
      ref={containerRef}
      className={`echart ${className ?? ""}`}
      role="img"
      aria-label={label}
    />
  );
}

const priorityMapOption: EChartsOption = {
  animationDuration: 550,
  animationEasing: "cubicOut",
  textStyle: sharedTextStyle,
  grid: { left: 42, right: 22, top: 28, bottom: 38 },
  tooltip: {
    trigger: "item",
    backgroundColor: colours.ink,
    borderWidth: 0,
    textStyle: { color: "#f7f7f2", fontSize: 11 },
    formatter:
      "<strong>{b}</strong><br/>Change: {@[0]}%<br/>Customer effort: {@[1]}/10<br/>{@[2]} interactions",
  },
  xAxis: {
    type: "value",
    min: -20,
    max: 45,
    name: "Change in contact volume",
    nameLocation: "middle",
    nameGap: 28,
    nameTextStyle: { color: colours.muted, fontSize: 9 },
    axisLabel: { color: colours.muted, fontSize: 8, formatter: "{value}%" },
    splitLine: { lineStyle: { color: colours.grid, type: "dashed" } },
    axisLine: { lineStyle: { color: "#bfc3bf" } },
  },
  yAxis: {
    type: "value",
    min: 3,
    max: 10,
    name: "Customer effort",
    nameLocation: "middle",
    nameGap: 29,
    nameTextStyle: { color: colours.muted, fontSize: 9 },
    axisLabel: { color: colours.muted, fontSize: 8 },
    splitLine: { lineStyle: { color: colours.grid, type: "dashed" } },
    axisLine: { show: false },
  },
  series: [
    {
      type: "scatter",
      emphasis: { focus: "self", scale: 1.08 },
      label: {
        show: true,
        position: "top",
        distance: 7,
        color: colours.ink,
        fontSize: 9,
        fontWeight: 600,
        formatter: "{b}",
      },
      markArea: {
        silent: true,
        itemStyle: { color: "rgba(168,75,63,.045)" },
        label: {
          show: true,
          position: "insideTopRight",
          color: "#9c6b64",
          fontSize: 8,
          formatter: "RISING · HIGH EFFORT",
        },
        data: [
          [
            { xAxis: 15, yAxis: 7 },
            { xAxis: 45, yAxis: 10 },
          ],
        ],
      },
      data: [
        {
          name: "Identity verification",
          value: [38, 8.8, 4210],
          symbolSize: 72,
          itemStyle: { color: colours.red, opacity: 0.82 },
        },
        {
          name: "Payment date",
          value: [24, 7.6, 5840],
          symbolSize: 83,
          itemStyle: { color: colours.amber, opacity: 0.82 },
        },
        {
          name: "Renewal explanation",
          value: [17, 6.9, 3760],
          symbolSize: 66,
          itemStyle: { color: colours.blue, opacity: 0.8 },
        },
        {
          name: "Refund progress",
          value: [6, 7.2, 2940],
          symbolSize: 58,
          itemStyle: { color: colours.teal, opacity: 0.8 },
        },
        {
          name: "Plan comparison",
          value: [8, 4.5, 3180],
          symbolSize: 60,
          itemStyle: { color: colours.green, opacity: 0.78 },
        },
        {
          name: "Password recovery",
          value: [-4, 6.1, 2680],
          symbolSize: 54,
          itemStyle: { color: "#64746f", opacity: 0.75 },
        },
        {
          name: "Order status",
          value: [-13, 3.8, 4920],
          symbolSize: 76,
          itemStyle: { color: colours.greenLight, opacity: 0.74 },
        },
      ],
    },
  ],
};

const wordColours = [
  colours.green,
  colours.blue,
  colours.amber,
  colours.teal,
  "#5d6d68",
  "#795e4c",
  "#3c6862",
];

const voiceCloudOption: EChartsOption = {
  animationDuration: 500,
  textStyle: sharedTextStyle,
  tooltip: {
    show: true,
    backgroundColor: colours.ink,
    borderWidth: 0,
    textStyle: { color: "#f7f7f2", fontSize: 11 },
    formatter: "{b}<br/><strong>{c} interactions</strong>",
  },
  series: [
    {
      type: "wordCloud",
      shape: "square",
      width: "98%",
      height: "94%",
      top: "3%",
      left: "1%",
      gridSize: 9,
      sizeRange: [12, 43],
      rotationRange: [0, 0],
      layoutAnimation: true,
      textStyle: {
        fontFamily: "Manrope",
        fontWeight: 600,
        color: (params) =>
          wordColours[(params?.dataIndex ?? 0) % wordColours.length] ??
          colours.green,
      },
      emphasis: { focus: "self", textStyle: { opacity: 0.65 } },
      data: [
        { name: "payment date", value: 5840 },
        { name: "identity check", value: 4210 },
        { name: "order status", value: 3920 },
        { name: "price increase", value: 3180 },
        { name: "refund progress", value: 2940 },
        { name: "document upload", value: 2680 },
        { name: "renewal letter", value: 2360 },
        { name: "unable to log in", value: 2010 },
        { name: "cancel my plan", value: 1820 },
        { name: "delivery delay", value: 1710 },
        { name: "duplicate warning", value: 1480 },
        { name: "change address", value: 1260 },
        { name: "missing refund", value: 1180 },
        { name: "compare plans", value: 1040 },
        { name: "collection date", value: 980 },
        { name: "app camera", value: 860 },
        { name: "speak to someone", value: 740 },
      ],
    },
  ],
};

const demandTrendOption: EChartsOption = {
  animationDuration: 500,
  animationEasing: "cubicOut",
  textStyle: sharedTextStyle,
  color: [colours.green, colours.amber, colours.blue],
  grid: { left: 45, right: 18, top: 34, bottom: 28 },
  legend: {
    top: 0,
    right: 0,
    itemWidth: 9,
    itemHeight: 3,
    textStyle: { color: colours.muted, fontSize: 9 },
  },
  tooltip: {
    trigger: "axis",
    backgroundColor: colours.ink,
    borderWidth: 0,
    textStyle: { color: "#f7f7f2", fontSize: 10 },
    valueFormatter: (value) => Number(value).toLocaleString(),
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: [
      "20 May",
      "27 May",
      "3 Jun",
      "10 Jun",
      "17 Jun",
      "24 Jun",
      "1 Jul",
      "8 Jul",
    ],
    axisLine: { lineStyle: { color: colours.grid } },
    axisTick: { show: false },
    axisLabel: { color: "#909793", fontSize: 8, margin: 12 },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 60000,
    interval: 20000,
    axisLabel: {
      color: "#909793",
      fontSize: 8,
      formatter: (value: number) => `${value / 1000}k`,
    },
    splitLine: { lineStyle: { color: colours.grid, type: "dashed" } },
  },
  series: [
    {
      name: "All interactions",
      type: "line",
      smooth: 0.22,
      showSymbol: false,
      lineStyle: { width: 2.2 },
      areaStyle: { color: "rgba(23,107,91,.08)" },
      data: [41600, 43800, 42100, 46700, 48100, 49600, 52200, 56480],
      markLine: {
        symbol: "none",
        lineStyle: { color: "#a84b3f", type: "dashed", width: 1 },
        label: {
          formatter: "Android 8.14",
          color: colours.red,
          fontSize: 8,
          position: "insideEndTop",
        },
        data: [{ xAxis: "1 Jul" }],
      },
    },
    {
      name: "Avoidable demand",
      type: "line",
      smooth: 0.22,
      showSymbol: false,
      lineStyle: { width: 1.8 },
      data: [11800, 12300, 12100, 13200, 14100, 14800, 15900, 17961],
    },
    {
      name: "Repeat contact",
      type: "line",
      smooth: 0.22,
      showSymbol: false,
      lineStyle: { width: 1.6 },
      data: [9700, 9500, 9300, 9600, 10100, 10400, 10600, 10505],
    },
  ],
};

const paretoOption: EChartsOption = {
  animationDuration: 450,
  textStyle: sharedTextStyle,
  grid: { left: 130, right: 42, top: 20, bottom: 25 },
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    backgroundColor: colours.ink,
    borderWidth: 0,
    textStyle: { color: "#f7f7f2", fontSize: 10 },
  },
  xAxis: [
    {
      type: "value",
      axisLabel: {
        color: "#909793",
        fontSize: 8,
        formatter: (value: number) => `${value / 1000}k`,
      },
      splitLine: { lineStyle: { color: colours.grid, type: "dashed" } },
    },
    {
      type: "value",
      min: 0,
      max: 100,
      show: false,
    },
  ],
  yAxis: {
    type: "category",
    inverse: true,
    data: [
      "Payment date",
      "Order status",
      "Identity check",
      "Policy renewal",
      "Plan comparison",
      "Refund progress",
      "Password recovery",
    ],
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: colours.ink, fontSize: 9 },
  },
  series: [
    {
      name: "Interactions",
      type: "bar",
      barWidth: 11,
      itemStyle: { color: colours.green, borderRadius: [0, 2, 2, 0] },
      data: [5840, 4920, 4210, 3760, 3180, 2940, 2680],
    },
    {
      name: "Cumulative share",
      type: "line",
      xAxisIndex: 1,
      symbolSize: 5,
      itemStyle: { color: colours.amber },
      lineStyle: { color: colours.amber, width: 1.5 },
      data: [10.3, 19, 26.5, 33.2, 38.8, 44, 48.7],
    },
  ],
};

export function PriorityMapChart() {
  return (
    <EChart
      option={priorityMapOption}
      className="priority-map-chart"
      label="Priority map plotting change in contact volume against customer effort, with bubble size representing interaction volume"
    />
  );
}

export function VoiceCloudChart() {
  return (
    <EChart
      option={voiceCloudOption}
      className="voice-cloud-chart"
      label="Semantically normalised phrases customers used most often"
    />
  );
}

export function DemandTrendChart() {
  return (
    <EChart
      option={demandTrendOption}
      className="demand-trend-chart"
      label="Eight week trend for all interactions, avoidable demand and repeat contact"
    />
  );
}

export function DriverParetoChart() {
  return (
    <EChart
      option={paretoOption}
      className="driver-pareto-chart"
      label="Contact driver volumes and cumulative share"
    />
  );
}
